import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiClient } from '../client';
import { appKeys } from '../query-keys';
import { toastApiError, toastRequestError } from '../mutations/toast-api-error';
import {
  beginListWrite,
  beginRowWrite,
  endListWrite,
  endRowWrite,
  patchRow,
  type ListWrite
} from './optimisticRowWrite';
import { useAuthStore } from '@/stores/auth-store';
import i18next from '@/lib/i18n';
import { translateErrorMessage } from '@/lib/errors';
import type { ApiError } from '@/lib/agent.types';
import {
  activateAppContract,
  deactivateAppContract,
  listInstalledAppsContract,
  updateAppSeatsContract,
  type ActivateAppResponse,
  type ListInstalledAppsResponse,
  type UpdateAppSeatsRequest,
  type UpdateAppSeatsResponse
} from '@bliks-io/contracts';

async function fetchInstalledApps(
  params?: string,
  signal?: AbortSignal
): Promise<ListInstalledAppsResponse['apps']> {
  const url = params
    ? `${listInstalledAppsContract.path}?${params}`
    : listInstalledAppsContract.path;
  const { data } = await apiClient.get<ListInstalledAppsResponse>(url, {
    signal
  });
  return data.apps;
}

export const installedAppsQueryOptions = {
  queryKey: appKeys.installed(),
  queryFn: ({ signal }: { signal?: AbortSignal } = {}) => fetchInstalledApps(undefined, signal)
};

export function useInstalledApps() {
  const { token } = useAuthStore();

  return useQuery({
    ...installedAppsQueryOptions,
    enabled: !!token
  });
}

/** Fetches all installed apps including disabled ones (for admin management) */
export function useAllInstalledApps() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: appKeys.installedAll(),
    queryFn: ({ signal }: { signal?: AbortSignal } = {}) => fetchInstalledApps('all=true', signal),
    enabled: !!token
  });
}

type InstalledApp = ListInstalledAppsResponse['apps'][number];

/**
 * Replaces the seat assignments of one app wholesale. The failure toast goes
 * through `translateErrorMessage` rather than a fixed key because the only
 * failure a system admin can act on — the cap being reached — is reported by
 * the server with the numbers in its metadata.
 */
export function useUpdateAppSeats() {
  const queryClient = useQueryClient();

  // The response interceptor rejects with an enriched ApiError, so the error
  // type is narrowed here rather than cast at the toast.
  return useMutation<
    UpdateAppSeatsResponse,
    ApiError,
    { appId: string } & UpdateAppSeatsRequest,
    ListWrite
  >({
    mutationFn: async ({ appId, userIds }) => {
      const path = updateAppSeatsContract.path.replace(':appId', appId);
      const { data } = await apiClient.put<UpdateAppSeatsResponse>(path, { userIds });
      return data;
    },
    onMutate: () => beginListWrite(queryClient, appKeys.installedAll()),
    onSuccess: (response, { appId }, write) => {
      toast.success(i18next.t('common.Apps.seatsSaved'));
      // Patch the management list in place: the panel drops its draft on
      // success, so without this the assigned count falls back to the pre-save
      // value until the list is re-read.
      patchRow<InstalledApp>(
        queryClient,
        { listKey: appKeys.installedAll(), appId },
        { seatLimit: response.app.seatLimit, assignedUserIds: response.app.assignedUserIds }
      );
      queryClient.invalidateQueries({ queryKey: appKeys.installed(), exact: true });
      void endListWrite(queryClient, write);
    },
    onError: (error, _variables, write) => {
      void endListWrite(queryClient, write);
      toastRequestError(error, translateErrorMessage(error, i18next.t));
    }
  });
}

/** Flips the switch in the management list before the POST confirms it. */
function useAppToggleMutation(
  contract: { path: string },
  enabled: boolean,
  successKey: string,
  errorKey: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: appKeys.toggleMutations(),
    mutationFn: async (appId: string) => {
      const path = contract.path.replace(':appId', appId);
      const { data } = await apiClient.post<ActivateAppResponse>(path, {});
      return data;
    },
    onMutate: (appId) =>
      beginRowWrite<InstalledApp>(
        queryClient,
        { listKey: appKeys.installedAll(), appId },
        { enabled }
      ),
    onSuccess: () => {
      toast.success(i18next.t(successKey));
      // The nav's list of enabled apps.
      queryClient.invalidateQueries({ queryKey: appKeys.installed(), exact: true });
    },
    onError: (error) => {
      toastApiError(error, errorKey);
    },
    onSettled: (_data, error, _appId, write) => {
      void endRowWrite(queryClient, write, error);
    }
  });
}

export function useActivateApp() {
  return useAppToggleMutation(
    activateAppContract,
    true,
    'common.Apps.enableSuccess',
    'common.Apps.enableFailed'
  );
}

export function useDeactivateApp() {
  return useAppToggleMutation(
    deactivateAppContract,
    false,
    'common.Apps.disableSuccess',
    'common.Apps.disableFailed'
  );
}
